import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BellIcon,
  CheckCircleIcon,
  FunnelIcon,
  ArrowPathIcon,
  UserPlusIcon,
  CurrencyDollarIcon,
  ShieldExclamationIcon,
  MegaphoneIcon,
  Cog6ToothIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  InboxIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useAllNotifications, useNotifications, Notification } from '../hooks/useNotifications';
import api from '../services/api';
import clsx from 'clsx';

// Category configuration
const categories = [
  { value: '', label: 'All Categories', icon: InboxIcon },
  { value: 'affiliate', label: 'Affiliates', icon: UserPlusIcon },
  { value: 'payment', label: 'Payments', icon: CurrencyDollarIcon },
  { value: 'conversion', label: 'Conversions', icon: ArrowPathIcon },
  { value: 'fraud', label: 'Fraud', icon: ShieldExclamationIcon },
  { value: 'campaign', label: 'Campaigns', icon: MegaphoneIcon },
  { value: 'system', label: 'System', icon: Cog6ToothIcon },
];

const priorities = [
  { value: '', label: 'All Priorities' },
  { value: 'high', label: 'High', color: 'bg-error-500' },
  { value: 'medium', label: 'Medium', color: 'bg-warning-500' },
  { value: 'low', label: 'Low', color: 'bg-primary-500' },
];

const readStatus = [
  { value: '', label: 'All' },
  { value: 'false', label: 'Unread' },
  { value: 'true', label: 'Read' },
];

// Category icons mapping
const categoryIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  affiliate: UserPlusIcon,
  payment: CurrencyDollarIcon,
  conversion: ArrowPathIcon,
  fraud: ShieldExclamationIcon,
  campaign: MegaphoneIcon,
  system: Cog6ToothIcon,
};

// Priority styles
const priorityStyles: Record<string, { bg: string; text: string; border: string }> = {
  high: {
    bg: 'bg-error-500/10',
    text: 'text-error-400',
    border: 'border-error-500/30',
  },
  medium: {
    bg: 'bg-warning-500/10',
    text: 'text-warning-400',
    border: 'border-warning-500/30',
  },
  low: {
    bg: 'bg-primary-500/10',
    text: 'text-primary-400',
    border: 'border-primary-500/30',
  },
};

// Format date
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days === 0) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } else if (days === 1) {
    return 'Yesterday';
  } else if (days < 7) {
    return date.toLocaleDateString([], { weekday: 'long' });
  } else {
    return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  }
}

interface NotificationRowProps {
  notification: Notification;
  onMarkAsRead: (id: string) => void;
}

function NotificationRow({ notification, onMarkAsRead }: NotificationRowProps) {
  const CategoryIcon = categoryIcons[notification.category] || Cog6ToothIcon;
  const styles = priorityStyles[notification.priority] || priorityStyles.medium;

  const handleMarkAsRead = async () => {
    if (!notification.isRead) {
      try {
        await api.patch(`/notifications/${notification.id}/read`);
        onMarkAsRead(notification.id);
      } catch (error) {
        console.error('Failed to mark as read:', error);
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={clsx(
        'flex items-start gap-4 p-4 rounded-xl transition-all duration-200',
        notification.isRead
          ? 'bg-[var(--bg-card)] hover:bg-[var(--bg-card)]/80'
          : `${styles.bg} border ${styles.border}`
      )}
    >
      {/* Icon */}
      <div
        className={clsx(
          'flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center',
          notification.isRead ? 'bg-[var(--bg-secondary)]' : styles.bg
        )}
      >
        <CategoryIcon
          className={clsx('h-5 w-5', notification.isRead ? 'text-[var(--text-muted)]' : styles.text)}
        />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3
                className={clsx(
                  'text-sm font-semibold',
                  notification.isRead ? 'text-[var(--text-secondary)]' : 'text-[var(--text-primary)]'
                )}
              >
                {notification.title}
              </h3>
              {notification.priority === 'high' && !notification.isRead && (
                <ExclamationTriangleIcon className="h-4 w-4 text-error-400" />
              )}
              {!notification.isRead && (
                <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase bg-primary-500 text-white rounded">
                  New
                </span>
              )}
            </div>
            <p
              className={clsx(
                'text-sm mt-1',
                notification.isRead ? 'text-[var(--text-muted)]' : 'text-[var(--text-secondary)]'
              )}
            >
              {notification.message}
            </p>
            <div className="flex items-center gap-3 mt-2">
              <span
                className={clsx(
                  'text-xs px-2 py-0.5 rounded-full capitalize',
                  notification.isRead ? 'bg-[var(--bg-secondary)] text-[var(--text-muted)]' : styles.bg,
                  notification.isRead ? '' : styles.text
                )}
              >
                {notification.category}
              </span>
              <span
                className={clsx(
                  'text-xs px-2 py-0.5 rounded-full capitalize',
                  styles.bg,
                  notification.isRead ? 'text-[var(--text-muted)]' : styles.text
                )}
              >
                {notification.priority} priority
              </span>
              <span className="text-xs text-[var(--text-muted)]">{formatDate(notification.createdAt)}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {notification.actionUrl && (
              <Link
                to={notification.actionUrl}
                className="px-3 py-1.5 text-xs font-medium text-primary-400 bg-primary-500/10 hover:bg-primary-500/20 rounded-lg transition-colors"
              >
                View
              </Link>
            )}
            {!notification.isRead && (
              <button
                onClick={handleMarkAsRead}
                className="p-1.5 rounded-lg text-[var(--text-muted)] hover:text-success-400 hover:bg-success-500/10 transition-colors"
                title="Mark as read"
              >
                <CheckCircleIcon className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function Notifications() {
  const { notifications, loading, pagination, filters, goToPage, updateFilters, refresh } =
    useAllNotifications();
  const { markAllAsRead, unreadCount } = useNotifications();
  const [localNotifications, setLocalNotifications] = useState<Notification[]>([]);

  // Sync with fetched notifications
  useEffect(() => {
    setLocalNotifications(notifications);
  }, [notifications]);

  const handleMarkAsRead = (id: string) => {
    setLocalNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, isRead: true, readAt: new Date().toISOString() } : n))
    );
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
    setLocalNotifications((prev) =>
      prev.map((n) => ({ ...n, isRead: true, readAt: n.readAt || new Date().toISOString() }))
    );
  };

  const handleFilterChange = (key: string, value: string) => {
    updateFilters({
      ...filters,
      [key]: value === '' ? undefined : key === 'isRead' ? value === 'true' : value,
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[var(--text-primary)]">Notifications</h1>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            View and manage all your notifications
          </p>
        </div>
        <div className="flex items-center gap-3">
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              className="px-4 py-2 text-sm font-medium text-primary-400 bg-primary-500/10 hover:bg-primary-500/20 rounded-xl transition-colors flex items-center gap-2"
            >
              <CheckCircleIcon className="h-4 w-4" />
              Mark all as read
            </button>
          )}
          <button
            onClick={refresh}
            className={clsx(
              'p-2 rounded-xl text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-card)] transition-colors',
              loading && 'animate-spin'
            )}
            title="Refresh"
          >
            <ArrowPathIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex items-center gap-2 mb-4">
          <FunnelIcon className="h-5 w-5 text-[var(--text-muted)]" />
          <span className="text-sm font-medium text-[var(--text-secondary)]">Filters</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {/* Category Filter */}
          <div>
            <label className="block text-xs font-medium text-[var(--text-muted)] mb-1">Category</label>
            <select
              value={filters.category || ''}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              className="input w-full"
            >
              {categories.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>

          {/* Priority Filter */}
          <div>
            <label className="block text-xs font-medium text-[var(--text-muted)] mb-1">Priority</label>
            <select
              value={filters.priority || ''}
              onChange={(e) => handleFilterChange('priority', e.target.value)}
              className="input w-full"
            >
              {priorities.map((pri) => (
                <option key={pri.value} value={pri.value}>
                  {pri.label}
                </option>
              ))}
            </select>
          </div>

          {/* Read Status Filter */}
          <div>
            <label className="block text-xs font-medium text-[var(--text-muted)] mb-1">Status</label>
            <select
              value={filters.isRead === undefined ? '' : String(filters.isRead)}
              onChange={(e) => handleFilterChange('isRead', e.target.value)}
              className="input w-full"
            >
              {readStatus.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div className="space-y-3">
        {loading ? (
          <div className="card p-8 text-center">
            <ArrowPathIcon className="h-8 w-8 mx-auto text-[var(--text-muted)] animate-spin" />
            <p className="mt-3 text-sm text-[var(--text-secondary)]">Loading notifications...</p>
          </div>
        ) : localNotifications.length > 0 ? (
          <>
            {localNotifications.map((notification) => (
              <NotificationRow
                key={notification.id}
                notification={notification}
                onMarkAsRead={handleMarkAsRead}
              />
            ))}
          </>
        ) : (
          <div className="card p-12 text-center">
            <BellIcon className="h-16 w-16 mx-auto text-[var(--text-muted)] opacity-50" />
            <h3 className="mt-4 text-lg font-semibold text-[var(--text-primary)]">No notifications</h3>
            <p className="mt-2 text-sm text-[var(--text-secondary)]">
              {filters.category || filters.priority || filters.isRead !== undefined
                ? 'Try adjusting your filters to see more notifications'
                : "You're all caught up! New notifications will appear here."}
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-[var(--border-color)] pt-4">
          <p className="text-sm text-[var(--text-muted)]">
            Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
            {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}{' '}
            notifications
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => goToPage(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="p-2 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-card)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                let pageNum: number;
                if (pagination.totalPages <= 5) {
                  pageNum = i + 1;
                } else if (pagination.page <= 3) {
                  pageNum = i + 1;
                } else if (pagination.page >= pagination.totalPages - 2) {
                  pageNum = pagination.totalPages - 4 + i;
                } else {
                  pageNum = pagination.page - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => goToPage(pageNum)}
                    className={clsx(
                      'w-8 h-8 rounded-lg text-sm font-medium transition-colors',
                      pagination.page === pageNum
                        ? 'bg-primary-500 text-white'
                        : 'text-[var(--text-muted)] hover:bg-[var(--bg-card)]'
                    )}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => goToPage(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages}
              className="p-2 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-card)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
